type HubspotQueue = unknown[][]

declare global {
  interface Window {
    _hsq?: HubspotQueue
  }
}

interface ProvidedHubspot {
  trackPage: (email?: string) => void
}

const getQueue = () => {
  window._hsq = window._hsq || []
  return window._hsq
}

export default defineNuxtPlugin({
  name: 'hubspot-tracking',
  dependsOn: ['cookiebot'],
  setup() {
    const { hubspotPortalId } = useRuntimeConfig().public

    if (!hubspotPortalId) {
      const disabled: ProvidedHubspot = { trackPage: () => {} }

      return { provide: { hubspot: disabled } }
    }

    const router = useRouter()
    const { $cookiebot } = useNuxtApp()
    const { logError } = useErrorHandling()

    const hasMarketingConsent = computed(() => {
      const cbInstance = $cookiebot.instance
      if (!cbInstance) return false

      return cbInstance.consented && cbInstance.consent.marketing
    })

    const isRequested = ref(false)

    const { status, onError } = useScript(
      {
        src: `https://js.hs-scripts.com/${hubspotPortalId}.js`,
        async: true,
        defer: true,
        type: 'text/javascript',
      },
      {
        trigger: computed(() => hasMarketingConsent.value && isRequested.value),
      }
    )

    onError(error => {
      logError('HubSpot tracking script loading failed', error)
    })

    // The queue is only read once the script loads, so queueing before consent sends nothing
    const trackPage = (email?: string) => {
      isRequested.value = true

      const queue = getQueue()
      if (email) queue.push(['identify', { email }])
      // path, not fullPath: the signup steps carry the confirmation token in the query
      queue.push(['setPath', router.currentRoute.value.path])

      if (status.value === 'loaded' && hasMarketingConsent.value) {
        queue.push(['trackPageView'])
      }
    }

    return { provide: { hubspot: { trackPage } satisfies ProvidedHubspot } }
  },
})
